// Enhanced notification sound service with multiple sound options
class NotificationSoundService {
  constructor() {
    this.sounds = {};
    this.enabled = true;
    this.volume = 0.7;
    this.audioContext = null;
    
    // Initialize sounds
    this.initializeSounds();
    this.loadSettings();
  }

  initializeSounds() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Preload the notification sound file
      this.loadNotificationSound();
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  async loadNotificationSound() {
    try {
      // Try to load the notification.mp3 file
      const audio = new Audio();
      audio.preload = 'auto';
      audio.volume = this.volume;
      
      // Try different paths for the notification sound
      const soundPaths = [
        '/notification.mp3',
        '/assets/notification.mp3',
        '/kifmis/src/assets/notification.mp3',
        './assets/notification.mp3'
      ];
      
      for (const path of soundPaths) {
        try {
          audio.src = path;
          await new Promise((resolve, reject) => {
            audio.addEventListener('canplaythrough', resolve, { once: true });
            audio.addEventListener('error', reject, { once: true });
            audio.load();
          });
          
          this.sounds.notification = audio;
          console.log('✅ Notification sound loaded from:', path);
          break;
        } catch (error) {
          console.warn('Failed to load sound from:', path);
        }
      }
    } catch (error) {
      console.warn('Could not load notification sound file, using generated sounds');
    }
  }

  // Generate simple beep sounds with different frequencies
  generateBeep(frequency = 800, duration = 200, type = 'sine') {
    if (!this.audioContext || !this.enabled) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = type;

      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.volume, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration / 1000);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.warn('Error playing notification sound:', error);
    }
  }

  // Play different sounds for different notification types
  async playSound(type = 'info') {
    if (!this.enabled) return;

    // First try to play the loaded notification sound
    if (this.sounds.notification) {
      try {
        this.sounds.notification.currentTime = 0;
        this.sounds.notification.volume = this.volume;
        await this.sounds.notification.play();
        return;
      } catch (error) {
        console.warn('Failed to play notification sound file, using generated sound');
      }
    }

    // Fallback to generated sounds based on type
    switch (type) {
      case 'urgent':
        // High-pitched rapid beeps for urgent notifications
        this.generateBeep(1200, 100, 'square');
        setTimeout(() => this.generateBeep(1400, 100, 'square'), 150);
        setTimeout(() => this.generateBeep(1200, 100, 'square'), 300);
        setTimeout(() => this.generateBeep(1400, 100, 'square'), 450);
        break;
        
      case 'important':
        // Medium-pitched double beep for important notifications
        this.generateBeep(900, 200, 'triangle');
        setTimeout(() => this.generateBeep(1100, 200, 'triangle'), 300);
        break;
        
      case 'success':
        // Pleasant ascending tone for success notifications
        this.generateBeep(600, 150, 'sine');
        setTimeout(() => this.generateBeep(750, 150, 'sine'), 200);
        setTimeout(() => this.generateBeep(900, 200, 'sine'), 400);
        break;
        
      case 'info':
      default:
        // Simple single beep for info notifications
        this.generateBeep(800, 300, 'sine');
        break;
    }
  }

  // Play a test sound
  playTestSound() {
    this.playSound('info');
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('notificationSoundsEnabled', enabled.toString());
    
    if (enabled) {
      // Play a test sound when enabling
      setTimeout(() => this.playTestSound(), 100);
    }
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('notificationVolume', this.volume.toString());
    
    // Update volume for loaded sounds
    if (this.sounds.notification) {
      this.sounds.notification.volume = this.volume;
    }
    
    // Play a test sound at new volume
    setTimeout(() => this.playTestSound(), 100);
  }

  // Load settings from localStorage
  loadSettings() {
    const savedEnabled = localStorage.getItem('notificationSoundsEnabled');
    const savedVolume = localStorage.getItem('notificationVolume');
    
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }
    
    if (savedVolume !== null) {
      this.volume = parseFloat(savedVolume);
    }
  }

  // Initialize audio context on user interaction (required by browsers)
  initializeOnUserInteraction() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    // Reload notification sound if needed
    if (!this.sounds.notification) {
      this.loadNotificationSound();
    }
  }

  // Get current settings
  getSettings() {
    return {
      enabled: this.enabled,
      volume: this.volume,
      hasAudioFile: !!this.sounds.notification
    };
  }
}

// Create singleton instance
const notificationSoundService = new NotificationSoundService();

// Load saved settings
notificationSoundService.loadSettings();

// Initialize audio context on first user interaction
document.addEventListener('click', () => {
  notificationSoundService.initializeOnUserInteraction();
}, { once: true });

// Also initialize on keydown for better coverage
document.addEventListener('keydown', () => {
  notificationSoundService.initializeOnUserInteraction();
}, { once: true });

// Initialize on page visibility change (when user returns to tab)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    notificationSoundService.initializeOnUserInteraction();
  }
});

export default notificationSoundService;